import React from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import {
  Badge,
  CohortHeatmap,
  HorizontalBarList,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatTile,
  TimeSeriesBarChart,
  TimeSeriesLineChart,
  type ChartSeries,
  type TimePoint,
} from '@org/ui';
import { useAnalytics } from '../analytics-context';
import { ReportCard } from '../report-card';
import { ReportTable } from '../report-table';
import { TimeSeriesCard } from '../time-series-card';
import { formatBucket, formatDate, formatDateTime, formatNumber, formatRate } from '../format';
import { tileDelta } from '../compare';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  joined: 'default',
  invited: 'secondary',
  'awaiting-terms': 'secondary',
  new: 'outline',
  disabled: 'destructive',
};

type SurvivalBy = 'none' | 'level' | 'teacher' | 'type';

export function RetentionReports() {
  const { t, i18n } = useTranslation('admin');
  const { input, compareInput, grain, search, rangeDays } = useAnalytics();
  const comparing = Boolean(compareInput);
  const lang = i18n.language;
  const vsLabel =
    search.compare === 'lastYear' ? t('analytics.compare.vsLastYear') : t('analytics.common.vsPrevious', { days: rangeDays });

  const nDay = trpc.analytics.retentionExtra.nDayRetention.useQuery(input);
  const cmpNDay = trpc.analytics.retentionExtra.nDayRetention.useQuery(compareInput ?? input, { enabled: comparing });
  const funnel = trpc.analytics.retention.statusFunnel.useQuery(input);
  const onboarding = trpc.analytics.retentionExtra.onboardingFunnel.useQuery(input);
  const cmpOnboarding = trpc.analytics.retentionExtra.onboardingFunnel.useQuery(compareInput ?? input, { enabled: comparing });
  const signups = trpc.analytics.retention.signups.useQuery(input);
  const cmpSignups = trpc.analytics.retention.signups.useQuery(compareInput ?? input, { enabled: comparing });
  const cohorts = trpc.analytics.retention.cohorts.useQuery(input);
  const [survivalBy, setSurvivalBy] = React.useState<SurvivalBy>('none');
  const survival = trpc.analytics.retentionExtra.survival.useQuery({ ...input, by: survivalBy });
  const lifetime = trpc.analytics.retentionExtra.lifetime.useQuery(input);
  const recency = trpc.analytics.retention.recency.useQuery(input);
  const engagement = trpc.analytics.retention.engagement.useQuery(input);
  const students = trpc.analytics.retention.students.useQuery(input);
  const [cohortView, setCohortView] = React.useState<'grid' | 'curves'>('grid');

  const signupSeries: ChartSeries[] = [
    { key: 'joined', label: t('analytics.retention.signups.series.joined') },
    { key: 'pending', label: t('analytics.retention.signups.series.pending') },
  ];

  const cohortPeriod = cohorts.data?.period === 'week' ? 'week' : 'month';
  const cohortPeriodLabel = cohortPeriod === 'week' ? t('analytics.retention.cohorts.week') : t('analytics.retention.cohorts.month');
  const cohortRows = cohorts.data?.rows ?? [];
  const curveCohorts = cohortRows.slice(-6);
  const curveSeries: ChartSeries[] = curveCohorts.map((r) => ({ key: r.cohort, label: formatBucket(r.cohort, cohortPeriod, lang) }));
  const curveData: TimePoint[] = (cohorts.data?.columns ?? []).map((offset) => {
    const point: TimePoint = { bucket: String(offset) };
    for (const r of curveCohorts) {
      const v = r.cells[offset];
      if (v !== null && v !== undefined) point[r.cohort] = v;
    }
    return point;
  });

  const lifetimeBandSeries: ChartSeries[] = [
    { key: 'active', label: t('analytics.retention.lifetime.stillActive') },
    { key: 'churned', label: t('analytics.retention.lifetime.left') },
  ];
  const lifetimeBandData: TimePoint[] = (lifetime.data?.bands ?? []).map((b) => ({ bucket: b.band, active: b.active, churned: b.churned }));

  const ob = onboarding.data;
  const cmpOb = comparing ? cmpOnboarding.data : undefined;

  return (
    <div className="space-y-6">
      {/* D7 / D30 / D90 */}
      <ReportCard
        title={t('analytics.retention.nday.title')}
        description={t('analytics.retention.nday.description')}
        isLoading={nDay.isLoading}
        error={nDay.error}
        isEmpty={nDay.data?.joiners === 0}
      >
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile label={t('analytics.retention.nday.joiners')} value={nDay.data?.joiners ?? null} hint={t('analytics.retention.nday.joinersHint')} />
          {(['d7', 'd30', 'd90'] as const).map((k) => (
            <StatTile
              key={k}
              label={t(`analytics.retention.nday.${k}`)}
              value={nDay.data?.[k].rate ?? null}
              format="percent"
              delta={tileDelta(nDay.data?.[k].rate, comparing ? cmpNDay.data?.[k].rate : undefined, vsLabel, { percentPoints: true })}
              hint={
                nDay.data
                  ? t('analytics.retention.nday.hint', { retained: nDay.data[k].retained, eligible: nDay.data[k].eligible })
                  : undefined
              }
            />
          ))}
        </div>
      </ReportCard>

      {/* Cohorts: grid or curves */}
      <ReportCard
        title={t('analytics.retention.cohorts.title')}
        description={t('analytics.retention.cohorts.description', { period: cohortPeriodLabel })}
        isLoading={cohorts.isLoading}
        error={cohorts.error}
        isEmpty={cohortRows.length === 0}
        actions={
          <div className="flex rounded-md border text-xs">
            {(['grid', 'curves'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setCohortView(v)}
                className={`px-2 py-1 ${cohortView === v ? 'bg-muted font-medium' : 'text-muted-foreground'}`}
                aria-pressed={cohortView === v}
              >
                {t(`analytics.retention.cohorts.view.${v}`)}
              </button>
            ))}
          </div>
        }
      >
        {cohortView === 'grid' ? (
          <CohortHeatmap
            rows={cohortRows.map((r) => ({
              key: r.cohort,
              label: formatBucket(r.cohort, cohortPeriod, lang),
              size: r.size,
              cells: r.cells,
            }))}
            columnLabels={(cohorts.data?.columns ?? []).map((c) => `${cohortPeriodLabel} ${c}`)}
            rowLabel={t('analytics.retention.cohorts.cohort')}
            sizeLabel={t('analytics.retention.cohorts.size')}
          />
        ) : (
          <>
            <TimeSeriesLineChart
              data={curveData}
              series={curveSeries}
              yDomain={[0, 100]}
              valueFormat={(n) => formatRate(n)}
              xLabelFormat={(b) => `${cohortPeriodLabel} ${b}`}
              emptyLabel={t('analytics.common.noData')}
            />
            <p className="mt-2 text-xs text-muted-foreground">{t('analytics.retention.cohorts.curvesHint')}</p>
          </>
        )}
      </ReportCard>

      {/* Survival + lifetime */}
      <div className="grid gap-6 lg:grid-cols-2">
        <ReportCard
          title={t('analytics.retention.survival.title')}
          description={t('analytics.retention.survival.description')}
          isLoading={survival.isLoading}
          error={survival.error}
          isEmpty={survival.data?.all.size === 0}
          actions={
            <Select value={survivalBy} onValueChange={(v) => setSurvivalBy(v as SurvivalBy)}>
              <SelectTrigger className="h-8 w-[150px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['none', 'level', 'teacher', 'type'] as const).map((v) => (
                  <SelectItem key={v} value={v}>
                    {t(`analytics.retention.survival.by.${v}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        >
          <CohortHeatmap
            rows={[
              ...(survivalBy === 'none' || !survival.data
                ? []
                : [
                    {
                      key: '__all',
                      label: t('analytics.retention.survival.all'),
                      size: survival.data.all.size,
                      cells: survival.data.all.checkpoints.map((c) => c.rate),
                    },
                  ]),
              ...(survival.data?.rows ?? []).map((r) => ({
                key: r.key,
                label: survivalBy === 'none' ? t('analytics.retention.survival.all') : survivalBy === 'type' ? t(`analytics.studentTypes.${r.label}`, { defaultValue: r.label }) : r.label,
                size: r.size,
                cells: r.checkpoints.map((c) => c.rate),
              })),
            ]}
            columnLabels={(survival.data?.months ?? [1, 3, 6, 12]).map((m) => t('analytics.retention.survival.month', { count: m }))}
            rowLabel={t(`analytics.retention.survival.by.${survivalBy}`)}
            sizeLabel={t('analytics.retention.cohorts.size')}
          />
          <p className="mt-2 text-xs text-muted-foreground">{t('analytics.retention.survival.hint')}</p>
        </ReportCard>

        <ReportCard
          title={t('analytics.retention.lifetime.title')}
          description={t('analytics.retention.lifetime.description')}
          isLoading={lifetime.isLoading}
          error={lifetime.error}
          isEmpty={lifetime.data?.students === 0}
        >
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile label={t('analytics.retention.lifetime.students')} value={lifetime.data?.students ?? null} />
            <StatTile
              label={t('analytics.retention.lifetime.stillActive')}
              value={lifetime.data && lifetime.data.students > 0 ? Math.round((lifetime.data.stillActive / lifetime.data.students) * 100) : null}
              format="percent"
              hint={lifetime.data ? t('analytics.retention.lifetime.stillActiveHint', { n: lifetime.data.stillActive }) : undefined}
            />
            <StatTile label={t('analytics.retention.lifetime.median')} value={lifetime.data?.medianMonths ?? null} hint={t('analytics.retention.lifetime.months')} />
            <StatTile label={t('analytics.retention.lifetime.medianLeft')} value={lifetime.data?.medianMonthsChurned ?? null} hint={t('analytics.retention.lifetime.months')} />
          </div>
          <TimeSeriesBarChart
            data={lifetimeBandData}
            series={lifetimeBandSeries}
            stacked
            height={180}
            xLabelFormat={(b) => t(`analytics.retention.lifetime.bands.${b}`)}
            emptyLabel={t('analytics.common.noData')}
          />
          <ReportTable
            rows={lifetime.data?.cohorts ?? []}
            rowKey={(r) => r.cohort}
            defaultSort="cohort"
            pageSize={6}
            columns={[
              { key: 'cohort', label: t('analytics.retention.cohorts.cohort'), value: (r) => r.cohort, render: (r) => formatBucket(r.cohort, 'month', lang) },
              { key: 'size', label: t('analytics.retention.cohorts.size'), value: (r) => r.size, align: 'right' },
              { key: 'active', label: t('analytics.retention.lifetime.stillActive'), value: (r) => r.stillActive, align: 'right' },
              { key: 'median', label: t('analytics.retention.lifetime.median'), value: (r) => r.medianMonths, align: 'right' },
            ]}
          />
        </ReportCard>
      </div>

      {/* Onboarding funnel + status */}
      <div className="grid gap-6 lg:grid-cols-2">
        <ReportCard
          title={t('analytics.retention.onboarding.title')}
          description={t('analytics.retention.onboarding.description')}
          isLoading={onboarding.isLoading}
          error={onboarding.error}
          isEmpty={ob?.created === 0}
        >
          <div className="mb-4 grid grid-cols-3 gap-3">
            <StatTile
              label={t('analytics.retention.onboarding.inviteRate')}
              value={ob?.inviteRate ?? null}
              format="percent"
              delta={tileDelta(ob?.inviteRate, cmpOb?.inviteRate, vsLabel, { percentPoints: true })}
            />
            <StatTile
              label={t('analytics.retention.onboarding.joinRate')}
              value={ob?.joinRate ?? null}
              format="percent"
              delta={tileDelta(ob?.joinRate, cmpOb?.joinRate, vsLabel, { percentPoints: true })}
            />
            <StatTile
              label={t('analytics.retention.onboarding.stuck')}
              value={ob ? ob.stuckInvited + ob.stuckNotInvited : null}
              hint={ob ? t('analytics.retention.onboarding.stuckHint', { invited: ob.stuckInvited, notInvited: ob.stuckNotInvited }) : undefined}
            />
          </div>
          <HorizontalBarList
            items={[
              { key: 'created', label: t('analytics.retention.onboarding.created'), value: ob?.created ?? 0 },
              { key: 'invited', label: t('analytics.retention.onboarding.invited'), value: ob?.invited ?? 0, secondary: formatRate(ob?.inviteRate) },
              { key: 'joined', label: t('analytics.retention.onboarding.joined'), value: ob?.joined ?? 0, secondary: formatRate(ob?.overallRate) },
            ]}
          />
          {ob && ob.stuck.length > 0 ? (
            <div className="mt-4">
              <ReportTable
                rows={ob.stuck}
                rowKey={(r) => r.userId}
                defaultSort="days"
                pageSize={8}
                columns={[
                  { key: 'name', label: t('analytics.common.student'), value: (r) => r.name },
                  { key: 'status', label: t('analytics.common.status'), value: (r) => r.status, render: (r) => <Badge variant="secondary">{t(`analytics.retention.statuses.${r.status}`)}</Badge> },
                  { key: 'created', label: t('analytics.retention.students.createdAt'), value: (r) => r.createdAt, render: (r) => formatDate(r.createdAt, lang) },
                  { key: 'days', label: t('analytics.retention.onboarding.daysWaiting'), value: (r) => r.daysWaiting, align: 'right' },
                ]}
              />
            </div>
          ) : null}
        </ReportCard>

        <ReportCard
          title={t('analytics.retention.funnel.title')}
          description={t('analytics.retention.funnel.description')}
          isLoading={funnel.isLoading}
          error={funnel.error}
          isEmpty={funnel.data?.every((r) => r.total === 0)}
        >
          <HorizontalBarList
            items={(funnel.data ?? []).map((r) => ({ key: r.status, label: t(`analytics.retention.statuses.${r.status}`), value: r.total }))}
          />
        </ReportCard>
      </div>

      <TimeSeriesCard
        title={t('analytics.retention.signups.title')}
        description={t('analytics.retention.signups.description')}
        data={signups.data}
        cmpData={cmpSignups.data}
        series={signupSeries}
        stacked
        isLoading={signups.isLoading}
        error={signups.error}
      />

      <ReportCard
        title={t('analytics.retention.recency.title')}
        description={t('analytics.retention.recency.description')}
        isLoading={recency.isLoading}
        error={recency.error}
        isEmpty={recency.data?.students.length === 0}
      >
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <HorizontalBarList
            items={(recency.data?.bands ?? []).map((b) => ({ key: b.band, label: t(`analytics.retention.recency.bands.${b.band}`), value: b.total }))}
          />
          <ReportTable
            rows={recency.data?.students ?? []}
            rowKey={(r) => r.userId}
            defaultSort="days"
            pageSize={15}
            searchKeys={['name', 'email']}
            columns={[
              { key: 'name', label: t('analytics.common.student'), value: (r) => r.name },
              { key: 'level', label: t('analytics.common.level'), value: (r) => r.levelName },
              { key: 'lastActivity', label: t('analytics.common.lastActivity'), value: (r) => r.lastActivityAt, render: (r) => formatDateTime(r.lastActivityAt, lang) },
              { key: 'lastPresent', label: t('analytics.retention.recency.lastPresent'), value: (r) => r.lastPresentAt, render: (r) => formatDate(r.lastPresentAt, lang) },
              {
                key: 'days',
                label: t('analytics.retention.recency.daysSilent'),
                value: (r) => r.daysSinceActivity ?? Number.MAX_SAFE_INTEGER,
                render: (r) => (r.daysSinceActivity === null ? t('analytics.common.never') : r.daysSinceActivity),
                align: 'right',
              },
            ]}
          />
        </div>
      </ReportCard>

      <ReportCard
        title={t('analytics.retention.engagement.title')}
        description={t('analytics.retention.engagement.description')}
        isLoading={engagement.isLoading}
        error={engagement.error}
        isEmpty={engagement.data?.length === 0}
      >
        <ReportTable
          rows={engagement.data ?? []}
          rowKey={(r) => r.userId}
          defaultSort="blocks"
          searchKeys={['name', 'email']}
          columns={[
            { key: 'name', label: t('analytics.common.student'), value: (r) => r.name },
            { key: 'email', label: t('analytics.common.email'), value: (r) => r.email, className: 'text-muted-foreground' },
            { key: 'level', label: t('analytics.common.level'), value: (r) => r.levelName },
            { key: 'classes', label: t('analytics.retention.engagement.classesVisited'), value: (r) => r.classesVisited, align: 'right' },
            { key: 'blocks', label: t('analytics.retention.engagement.blocksVisited'), value: (r) => r.blocksVisited, align: 'right' },
            { key: 'submissions', label: t('analytics.retention.engagement.submissions'), value: (r) => r.submissions, align: 'right' },
            { key: 'chat', label: t('analytics.retention.engagement.chatMessages'), value: (r) => r.chatMessages, align: 'right' },
            { key: 'lastVisit', label: t('analytics.retention.engagement.lastVisit'), value: (r) => r.lastVisitAt, render: (r) => formatDateTime(r.lastVisitAt, lang) },
          ]}
        />
      </ReportCard>

      <ReportCard
        title={t('analytics.retention.students.title')}
        description={t('analytics.retention.students.description')}
        isLoading={students.isLoading}
        error={students.error}
        isEmpty={students.data?.length === 0}
      >
        <ReportTable
          rows={students.data ?? []}
          rowKey={(r) => r.userId}
          defaultSort="created"
          searchKeys={['name', 'email', 'status']}
          columns={[
            { key: 'name', label: t('analytics.common.student'), value: (r) => r.name },
            { key: 'email', label: t('analytics.common.email'), value: (r) => r.email, className: 'text-muted-foreground' },
            {
              key: 'status',
              label: t('analytics.common.status'),
              value: (r) => r.status,
              render: (r) => <Badge variant={STATUS_VARIANT[r.status] ?? 'outline'}>{t(`analytics.retention.statuses.${r.status}`)}</Badge>,
            },
            { key: 'type', label: t('analytics.common.type'), value: (r) => r.type, render: (r) => t(`analytics.studentTypes.${r.type}`, { defaultValue: r.type }) },
            { key: 'level', label: t('analytics.common.level'), value: (r) => r.levelName },
            { key: 'created', label: t('analytics.retention.students.createdAt'), value: (r) => r.createdAt, render: (r) => formatDate(r.createdAt, lang) },
            { key: 'lastActivity', label: t('analytics.common.lastActivity'), value: (r) => r.lastActivityAt, render: (r) => formatDateTime(r.lastActivityAt, lang) },
          ]}
        />
        <p className="mt-2 text-xs text-muted-foreground">
          {t('analytics.common.rows', { count: students.data?.length ?? 0, formatted: formatNumber(students.data?.length ?? 0, lang) })}
        </p>
      </ReportCard>
    </div>
  );
}
